import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Paper,
    Divider,
    Menu,
    MenuItem,
} from "@mui/material";
import northArrow from "../../../assets/icons/north_arrow.svg";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import FlipIcon from "@mui/icons-material/Flip";
import CreateIcon from "@mui/icons-material/Create";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import WindowIcon from "@mui/icons-material/Window";
import SquareIcon from "@mui/icons-material/Square";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import StraightenIcon from "@mui/icons-material/Straighten";

const GRID_SIZE = 20;

/**
 * SimpleFloorPlan Component
 *
 * Saves floor plan data in a portable format with metadata for accurate recreation in other programs.
 *
 * Data Format:
 * {
 *   version: "1.0",
 *   metadata: {
 *     created: ISO timestamp,
 *     canvasWidth: number (pixels),
 *     canvasHeight: number (pixels),
 *     gridSize: number (pixels),
 *     units: "pixels"
 *   },
 *   shapes: [array of shape objects]
 * }
 *
 * The component automatically saves data in this format and is backward compatible with old format (array only).
 */
const SimpleFloorPlan = ({ initialData, onChange }) => {
    const canvasRef = useRef(null);
    const lastInteractionTime = useRef(0);
    const [shapes, setShapes] = useState(
        (initialData && initialData.shapes) || [],
    );
    const [selectedShape, setSelectedShape] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [drawMode, setDrawMode] = useState(null);
    const [wallPoints, setWallPoints] = useState([]);
    const [placeMode, setPlaceMode] = useState(null);
    const [windowMenuAnchor, setWindowMenuAnchor] = useState(null);
    const [deleteMenuAnchor, setDeleteMenuAnchor] = useState(null);
    const [doorMenuAnchor, setDoorMenuAnchor] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [angleSnap, setAngleSnap] = useState(true);
    const [canvasDimensions, setCanvasDimensions] = useState({
        width: 0,
        height: 0,
    });
    const [dataLoaded, setDataLoaded] = useState(false);

    // Update canvas dimensions when canvas is rendered
    useEffect(() => {
        if (canvasRef.current) {
            const updateDimensions = () => {
                const rect = canvasRef.current.getBoundingClientRect();
                setCanvasDimensions({ width: rect.width, height: rect.height });
            };
            updateDimensions();
            window.addEventListener("resize", updateDimensions);
            return () => window.removeEventListener("resize", updateDimensions);
        }
    }, []);

    // Create floor plan data with metadata
    const getFloorPlanData = () => {
        return {
            version: "1.0",
            metadata: {
                created: new Date().toISOString(),
                canvasWidth: canvasDimensions.width,
                canvasHeight: canvasDimensions.height,
                gridSize: GRID_SIZE,
                units: "pixels",
            },
            shapes: shapes,
            svg: generateSVG(),
        };
    };

    // Generate SVG representation
    const generateSVG = () => {
        const { width, height } = canvasDimensions;
        if (width === 0 || height === 0) return null;

        // Calculate bounding box of all shapes to center the content
        let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;

        shapes.forEach((shape) => {
            if (
                shape.type === "wall" &&
                shape.points &&
                shape.points.length >= 2
            ) {
                shape.points.forEach((p) => {
                    minX = Math.min(minX, p.x);
                    minY = Math.min(minY, p.y);
                    maxX = Math.max(maxX, p.x);
                    maxY = Math.max(maxY, p.y);
                });
            } else if (
                shape.type === "door" ||
                shape.type === "window" ||
                shape.type === "panel" ||
                shape.type === "magnetAccess"
            ) {
                // Account for rotated shapes - calculate bounding box after rotation
                const rotation = shape.rotation || 0;
                const centerX = shape.x + shape.width / 2;
                const centerY = shape.y + shape.height / 2;

                // Calculate corners of the unrotated rectangle
                const corners = [
                    { x: shape.x, y: shape.y },
                    { x: shape.x + shape.width, y: shape.y },
                    { x: shape.x + shape.width, y: shape.y + shape.height },
                    { x: shape.x, y: shape.y + shape.height },
                ];

                // Rotate corners and find bounds
                corners.forEach((corner) => {
                    const dx = corner.x - centerX;
                    const dy = corner.y - centerY;
                    const rotatedX =
                        centerX +
                        dx * Math.cos(rotation) -
                        dy * Math.sin(rotation);
                    const rotatedY =
                        centerY +
                        dx * Math.sin(rotation) +
                        dy * Math.cos(rotation);
                    minX = Math.min(minX, rotatedX);
                    minY = Math.min(minY, rotatedY);
                    maxX = Math.max(maxX, rotatedX);
                    maxY = Math.max(maxY, rotatedY);
                });

                // Add extra padding for labels and door swings
                const padding = shape.type === "door" ? shape.width + 20 : 20;
                minY = Math.min(minY, shape.y - padding);
                maxY = Math.max(maxY, shape.y + shape.height + padding);
            }
        });

        // Calculate centering offset
        const contentWidth = maxX - minX;
        const contentHeight = maxY - minY;
        const offsetX = (width - contentWidth) / 2 - minX;
        const offsetY = (height - contentHeight) / 2 - minY;

        let svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
`;

        // White background only (no grid, no north arrow)
        svgContent += `  <rect width="100%" height="100%" fill="white"/>
`;

        // Wrap all shapes in a group with centering transform
        svgContent += `  <g transform="translate(${offsetX}, ${offsetY})">
`;

        // Add shapes
        shapes.forEach((shape) => {
            if (
                shape.type === "wall" &&
                shape.points &&
                shape.points.length >= 2
            ) {
                const pathData = `M ${shape.points
                    .map((p) => `${p.x},${p.y}`)
                    .join(" L ")}`;
                // Dark border (thicker)
                svgContent += `  <path d="${pathData}" stroke="#333" stroke-width="8" fill="none" stroke-linecap="square" stroke-linejoin="miter"/>
`;
                // White center (thinner)
                svgContent += `  <path d="${pathData}" stroke="#FFFFFF" stroke-width="4" fill="none" stroke-linecap="square" stroke-linejoin="miter"/>
`;
            } else if (shape.type === "door") {
                const rotation = (shape.rotation || 0) * (180 / Math.PI);
                const centerX = shape.x + shape.width / 2;
                const centerY = shape.y + shape.height / 2;
                const doorWidth = shape.width;
                const doorHeight = shape.height;
                const isSlidingDoor = shape.doorType === "sliding";
                const isDoubleDoor = !isSlidingDoor && doorWidth >= 70;
                const halfWidth = doorWidth / 2;

                // Calculate label rotation (flip if upside down)
                const normalizedDeg = ((rotation % 360) + 360) % 360;
                const labelRotation =
                    normalizedDeg > 90 && normalizedDeg <= 270 ? 180 : 0;

                svgContent += `  <g transform="rotate(${rotation} ${centerX} ${centerY})">
`;
                // Door frame (white fill with dark border)
                svgContent += `    <rect x="${shape.x}" y="${shape.y}" width="${doorWidth}" height="${doorHeight}" fill="#FFFFFF" stroke="#333" stroke-width="1"/>
`;

                if (isSlidingDoor) {
                    // SLIDING DOOR
                    svgContent += `    <line x1="${shape.x}" y1="${
                        shape.y
                    }" x2="${shape.x + doorWidth}" y2="${
                        shape.y
                    }" stroke="#666" stroke-width="2"/>
`;
                    svgContent += `    <rect x="${shape.x + 5}" y="${
                        shape.y
                    }" width="${halfWidth -
                        7}" height="${doorHeight}" fill="none" stroke="#666" stroke-width="2"/>
`;
                    svgContent += `    <rect x="${shape.x +
                        halfWidth +
                        2}" y="${shape.y}" width="${halfWidth -
                        7}" height="${doorHeight}" fill="none" stroke="#666" stroke-width="2"/>
`;
                    // Left arrow
                    svgContent += `    <path d="M ${shape.x +
                        halfWidth / 2} ${shape.y +
                        doorHeight +
                        10} L ${shape.x + 10} ${shape.y +
                        doorHeight +
                        10} M ${shape.x + 10} ${shape.y +
                        doorHeight +
                        10} L ${shape.x + 15} ${shape.y +
                        doorHeight +
                        5} M ${shape.x + 10} ${shape.y +
                        doorHeight +
                        10} L ${shape.x + 15} ${shape.y +
                        doorHeight +
                        15}" stroke="#333" stroke-width="2" fill="none"/>
`;
                    // Right arrow
                    svgContent += `    <path d="M ${shape.x +
                        halfWidth +
                        halfWidth / 2} ${shape.y +
                        doorHeight +
                        10} L ${shape.x + doorWidth - 10} ${shape.y +
                        doorHeight +
                        10} M ${shape.x + doorWidth - 10} ${shape.y +
                        doorHeight +
                        10} L ${shape.x + doorWidth - 15} ${shape.y +
                        doorHeight +
                        5} M ${shape.x + doorWidth - 10} ${shape.y +
                        doorHeight +
                        10} L ${shape.x + doorWidth - 15} ${shape.y +
                        doorHeight +
                        15}" stroke="#333" stroke-width="2" fill="none"/>
`;
                    // Label
                    svgContent += `    <text x="${shape.x +
                        doorWidth / 2}" y="${shape.y -
                        10}" text-anchor="middle" font-family="Arial" font-size="11" font-weight="bold" fill="#333" transform="rotate(${labelRotation} ${shape.x +
                        doorWidth / 2} ${shape.y - 10})">SLIDING DOOR</text>
`;
                } else if (isDoubleDoor) {
                    // DOUBLE DOOR
                    // Left swing arc
                    const leftArcPath = shape.flipped
                        ? `M ${shape.x + halfWidth} ${shape.y +
                              doorHeight /
                                  2} A ${halfWidth} ${halfWidth} 0 0 1 ${
                              shape.x
                          } ${shape.y + doorHeight / 2 + halfWidth}`
                        : `M ${shape.x + halfWidth} ${shape.y +
                              doorHeight /
                                  2} A ${halfWidth} ${halfWidth} 0 0 0 ${
                              shape.x
                          } ${shape.y + doorHeight / 2 - halfWidth}`;
                    svgContent += `    <path d="${leftArcPath}" fill="none" stroke="#666" stroke-width="1.5" stroke-dasharray="3,3"/>
`;
                    // Left panel line
                    const leftY2 = shape.flipped
                        ? shape.y + doorHeight / 2 + halfWidth
                        : shape.y + doorHeight / 2 - halfWidth;
                    svgContent += `    <line x1="${shape.x}" y1="${shape.y +
                        doorHeight / 2}" x2="${
                        shape.x
                    }" y2="${leftY2}" stroke="#333" stroke-width="2"/>
`;
                    // Right swing arc
                    const rightArcPath = shape.flipped
                        ? `M ${shape.x + halfWidth} ${shape.y +
                              doorHeight /
                                  2} A ${halfWidth} ${halfWidth} 0 0 0 ${shape.x +
                              doorWidth} ${shape.y +
                              doorHeight / 2 +
                              halfWidth}`
                        : `M ${shape.x + halfWidth} ${shape.y +
                              doorHeight /
                                  2} A ${halfWidth} ${halfWidth} 0 0 1 ${shape.x +
                              doorWidth} ${shape.y +
                              doorHeight / 2 -
                              halfWidth}`;
                    svgContent += `    <path d="${rightArcPath}" fill="none" stroke="#666" stroke-width="1.5" stroke-dasharray="3,3"/>
`;
                    // Right panel line
                    const rightY2 = shape.flipped
                        ? shape.y + doorHeight / 2 + halfWidth
                        : shape.y + doorHeight / 2 - halfWidth;
                    svgContent += `    <line x1="${shape.x +
                        doorWidth}" y1="${shape.y +
                        doorHeight / 2}" x2="${shape.x +
                        doorWidth}" y2="${rightY2}" stroke="#333" stroke-width="2"/>
`;
                    // Label (below door for unflipped double doors, above for others)
                    const labelY = !shape.flipped
                        ? shape.y + doorHeight + 15
                        : shape.y - 10;
                    svgContent += `    <text x="${shape.x +
                        doorWidth /
                            2}" y="${labelY}" text-anchor="middle" font-family="Arial" font-size="11" font-weight="bold" fill="#333" transform="rotate(${labelRotation} ${shape.x +
                        doorWidth / 2} ${labelY})">DOOR</text>
`;
                } else {
                    // SINGLE DOOR
                    const arcPath = shape.flipped
                        ? `M ${shape.x + doorWidth} ${shape.y +
                              doorHeight /
                                  2} A ${doorWidth} ${doorWidth} 0 0 1 ${
                              shape.x
                          } ${shape.y + doorHeight / 2 + doorWidth}`
                        : `M ${shape.x + doorWidth} ${shape.y +
                              doorHeight /
                                  2} A ${doorWidth} ${doorWidth} 0 0 0 ${
                              shape.x
                          } ${shape.y + doorHeight / 2 - doorWidth}`;
                    svgContent += `    <path d="${arcPath}" fill="none" stroke="#666" stroke-width="1.5" stroke-dasharray="3,3"/>
`;
                    // Panel line
                    const y2 = shape.flipped
                        ? shape.y + doorHeight / 2 + doorWidth
                        : shape.y + doorHeight / 2 - doorWidth;
                    svgContent += `    <line x1="${shape.x}" y1="${shape.y +
                        doorHeight / 2}" x2="${
                        shape.x
                    }" y2="${y2}" stroke="#333" stroke-width="2"/>
`;
                    // Label (above door)
                    svgContent += `    <text x="${shape.x +
                        doorWidth / 2}" y="${shape.y -
                        10}" text-anchor="middle" font-family="Arial" font-size="11" font-weight="bold" fill="#333" transform="rotate(${labelRotation} ${shape.x +
                        doorWidth / 2} ${shape.y - 10})">DOOR</text>
`;
                }
                svgContent += `  </g>
`;
            } else if (shape.type === "window") {
                const rotation = (shape.rotation || 0) * (180 / Math.PI);
                const centerX = shape.x + shape.width / 2;
                const centerY = shape.y + shape.height / 2;
                const normalizedDeg = ((rotation % 360) + 360) % 360;
                const labelRotation =
                    normalizedDeg > 90 && normalizedDeg <= 270 ? 180 : 0;

                svgContent += `  <g transform="rotate(${rotation} ${centerX} ${centerY})">
`;
                svgContent += `    <rect x="${shape.x}" y="${shape.y}" width="${shape.width}" height="${shape.height}" fill="#D3D3D3" stroke="#333" stroke-width="1"/>
`;
                // Label above window
                svgContent += `    <text x="${centerX}" y="${shape.y -
                    10}" text-anchor="middle" font-family="Arial" font-size="11" font-weight="bold" fill="#333" transform="rotate(${labelRotation} ${centerX} ${shape.y -
                    10})">WINDOW</text>
`;
                svgContent += `  </g>
`;
            } else if (shape.type === "panel") {
                const rotation = (shape.rotation || 0) * (180 / Math.PI);
                const centerX = shape.x + shape.width / 2;
                const centerY = shape.y + shape.height / 2;
                const normalizedDeg = ((rotation % 360) + 360) % 360;
                const labelRotation =
                    normalizedDeg > 90 && normalizedDeg <= 270 ? 180 : 0;

                svgContent += `  <g transform="rotate(${rotation} ${centerX} ${centerY})">
`;
                svgContent += `    <rect x="${shape.x}" y="${shape.y}" width="${
                    shape.width
                }" height="${shape.height}" fill="${shape.color ||
                    "#D3D3D3"}" stroke="#333" stroke-width="1"/>
`;
                // Text inside panel
                svgContent += `    <text x="${centerX}" y="${centerY -
                    5}" text-anchor="middle" font-family="Arial" font-size="11" font-weight="bold" fill="#333" transform="rotate(${labelRotation} ${centerX} ${centerY})">PEN</text>
`;
                svgContent += `    <text x="${centerX}" y="${centerY +
                    8}" text-anchor="middle" font-family="Arial" font-size="11" font-weight="bold" fill="#333" transform="rotate(${labelRotation} ${centerX} ${centerY})">PANEL</text>
`;
                svgContent += `  </g>
`;
            } else if (shape.type === "magnetAccess") {
                const rotation = (shape.rotation || 0) * (180 / Math.PI);
                const centerX = shape.x + shape.width / 2;
                const centerY = shape.y + shape.height / 2;
                const normalizedDeg = ((rotation % 360) + 360) % 360;
                const labelRotation =
                    normalizedDeg > 90 && normalizedDeg <= 270 ? 180 : 0;

                svgContent += `  <g transform="rotate(${rotation} ${centerX} ${centerY})">
`;
                svgContent += `    <rect x="${shape.x}" y="${shape.y}" width="${
                    shape.width
                }" height="${shape.height}" fill="${shape.color ||
                    "#D3D3D3"}" stroke="#333" stroke-width="1"/>
`;
                // Label above magnet access (same as window)
                svgContent += `    <text x="${centerX}" y="${shape.y -
                    10}" text-anchor="middle" font-family="Arial" font-size="11" font-weight="bold" fill="#333" transform="rotate(${labelRotation} ${centerX} ${shape.y -
                    10})">MAGNET ACCESS</text>
`;
                svgContent += `  </g>
`;
            }
        });

        // Close the centering group
        svgContent += `  </g>
`;
        svgContent += `</svg>`;
        return svgContent;
    };

    // Notify parent of changes with full metadata whenever shapes change
    useEffect(() => {
        if (onChange && canvasDimensions.width > 0) {
            onChange(getFloorPlanData());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shapes, canvasDimensions]);

    // Center shapes on the canvas without scaling
    const centerShapes = (shapes, canvasWidth, canvasHeight) => {
        if (!shapes || shapes.length === 0 || !canvasWidth || !canvasHeight) {
            return shapes;
        }

        // Calculate bounding box of all shapes
        let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;

        shapes.forEach((shape) => {
            if (shape.type === "wall" && shape.points) {
                shape.points.forEach((point) => {
                    minX = Math.min(minX, point.x);
                    minY = Math.min(minY, point.y);
                    maxX = Math.max(maxX, point.x);
                    maxY = Math.max(maxY, point.y);
                });
            } else if (
                shape.type === "door" ||
                shape.type === "window" ||
                shape.type === "panel" ||
                shape.type === "magnetAccess"
            ) {
                // Account for rotated shapes
                const rotation = shape.rotation || 0;
                const centerX = shape.x + shape.width / 2;
                const centerY = shape.y + shape.height / 2;

                const corners = [
                    { x: shape.x, y: shape.y },
                    { x: shape.x + shape.width, y: shape.y },
                    { x: shape.x + shape.width, y: shape.y + shape.height },
                    { x: shape.x, y: shape.y + shape.height },
                ];

                corners.forEach((corner) => {
                    const dx = corner.x - centerX;
                    const dy = corner.y - centerY;
                    const rotatedX =
                        centerX +
                        dx * Math.cos(rotation) -
                        dy * Math.sin(rotation);
                    const rotatedY =
                        centerY +
                        dx * Math.sin(rotation) +
                        dy * Math.cos(rotation);
                    minX = Math.min(minX, rotatedX);
                    minY = Math.min(minY, rotatedY);
                    maxX = Math.max(maxX, rotatedX);
                    maxY = Math.max(maxY, rotatedY);
                });
            }
        });

        // If no valid bounds, return shapes as-is
        if (
            !isFinite(minX) ||
            !isFinite(minY) ||
            !isFinite(maxX) ||
            !isFinite(maxY)
        ) {
            return shapes;
        }

        // Calculate center of shapes bounding box
        const shapesCenterX = (minX + maxX) / 2;
        const shapesCenterY = (minY + maxY) / 2;

        // Calculate center of canvas
        const canvasCenterX = canvasWidth / 2;
        const canvasCenterY = canvasHeight / 2;

        // Calculate offset to center shapes on canvas
        const offsetX = canvasCenterX - shapesCenterX;
        const offsetY = canvasCenterY - shapesCenterY;

        // Apply offset to all shapes (no scaling, just repositioning)
        return shapes.map((shape) => {
            if (shape.type === "wall" && shape.points) {
                return {
                    ...shape,
                    points: shape.points.map((point) => ({
                        x: point.x + offsetX,
                        y: point.y + offsetY,
                    })),
                };
            } else if (
                shape.type === "door" ||
                shape.type === "window" ||
                shape.type === "panel" ||
                shape.type === "magnetAccess"
            ) {
                return {
                    ...shape,
                    x: shape.x + offsetX,
                    y: shape.y + offsetY,
                };
            }
            return shape;
        });
    };

    // Load from initialData once the canvas is ready
    useEffect(() => {
        if (
            dataLoaded ||
            canvasDimensions.width === 0 ||
            canvasDimensions.height === 0
        ) {
            return;
        }

        if (initialData) {
            let loadedShapes = [];

            if (Array.isArray(initialData)) {
                loadedShapes = initialData;
            } else if (
                initialData.shapes &&
                Array.isArray(initialData.shapes)
            ) {
                loadedShapes = initialData.shapes;
            }

            // Center the shapes on the current canvas
            const centeredShapes = centerShapes(
                loadedShapes,
                canvasDimensions.width,
                canvasDimensions.height,
            );
            setShapes(centeredShapes);
        }

        setDataLoaded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvasDimensions, initialData, dataLoaded]);

    const snapToGrid = (val) => Math.round(val / GRID_SIZE) * GRID_SIZE;

    // Snap point to horizontal or vertical alignment with previous point
    const snapToAngle = (x, y, prevPoint) => {
        if (!angleSnap || !prevPoint) return { x, y };

        const dx = x - prevPoint.x;
        const dy = y - prevPoint.y;

        // Calculate angle in degrees
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);

        // Snap threshold in degrees (if within 15° of horizontal/vertical, snap to it)
        const snapThreshold = 15;

        // Check if close to horizontal (0° or 180°)
        if (
            Math.abs(angle) < snapThreshold ||
            Math.abs(Math.abs(angle) - 180) < snapThreshold
        ) {
            return { x, y: prevPoint.y }; // Keep same Y
        }

        // Check if close to vertical (90° or -90°)
        if (
            Math.abs(angle - 90) < snapThreshold ||
            Math.abs(angle + 90) < snapThreshold
        ) {
            return { x: prevPoint.x, y }; // Keep same X
        }

        return { x, y }; // No snap, return original position
    };

    const findNearestWall = (x, y) => {
        const threshold = 30;
        for (let shape of shapes) {
            if (shape.type === "wall") {
                for (let i = 0; i < shape.points.length - 1; i++) {
                    const p1 = shape.points[i];
                    const p2 = shape.points[i + 1];
                    const dist = distanceToSegment(
                        x,
                        y,
                        p1.x,
                        p1.y,
                        p2.x,
                        p2.y,
                    );
                    if (dist < threshold) {
                        return { shape, segmentIndex: i, p1, p2 };
                    }
                }
            }
        }
        return null;
    };

    const distanceToSegment = (px, py, x1, y1, x2, y2) => {
        const A = px - x1;
        const B = py - y1;
        const C = x2 - x1;
        const D = y2 - y1;
        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        let param = -1;
        if (lenSq !== 0) param = dot / lenSq;
        let xx, yy;
        if (param < 0) {
            xx = x1;
            yy = y1;
        } else if (param > 1) {
            xx = x2;
            yy = y2;
        } else {
            xx = x1 + param * C;
            yy = y1 + param * D;
        }
        const dx = px - xx;
        const dy = py - yy;
        return Math.sqrt(dx * dx + dy * dy);
    };

    const placeShapeAt = (x, y) => {
        const nearWall = findNearestWall(x, y);
        let finalX = x;
        let finalY = y;
        let rotation = 0;

        if (
            nearWall &&
            (placeMode.type === "door" ||
                placeMode.type === "window" ||
                placeMode.type === "magnetAccess")
        ) {
            const { p1, p2 } = nearWall;
            const midX = (p1.x + p2.x) / 2;
            const midY = (p1.y + p2.y) / 2;
            const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
            // Rotate door parallel to the wall
            rotation = angle;
            finalX = midX;
            finalY = midY;
        }

        setShapes([
            ...shapes,
            {
                ...placeMode,
                id: Date.now(),
                x: finalX - placeMode.width / 2,
                y: finalY - placeMode.height / 2,
                rotation: rotation,
            },
        ]);
        setPlaceMode(null);
    };

    const handleCanvasClick = (e) => {
        // Prevent duplicate events from touch + click
        const now = Date.now();
        if (now - lastInteractionTime.current < 300) {
            return; // Ignore if within 300ms of last interaction
        }
        lastInteractionTime.current = now;

        const rect = canvasRef.current.getBoundingClientRect();
        let x = snapToGrid(e.clientX - rect.left);
        let y = snapToGrid(e.clientY - rect.top);

        if (drawMode === "wall") {
            if (wallPoints.length > 0) {
                const snapped = snapToAngle(
                    x,
                    y,
                    wallPoints[wallPoints.length - 1],
                );
                x = snapped.x;
                y = snapped.y;
            }
            setWallPoints([...wallPoints, { x, y }]);
        } else if (placeMode) {
            placeShapeAt(x, y);
        } else {
            setSelectedShape(null);
        }
    };

    const handleCanvasTouch = (e) => {
        // Prevent the synthetic click event that follows touch
        e.preventDefault();

        const now = Date.now();
        if (now - lastInteractionTime.current < 300) {
            return;
        }
        lastInteractionTime.current = now;

        const rect = canvasRef.current.getBoundingClientRect();
        const clientX = e.touches && e.touches[0] && e.touches[0].clientX;
        const clientY = e.touches && e.touches[0] && e.touches[0].clientY;
        if (!clientX || !clientY) return;

        let x = snapToGrid(clientX - rect.left);
        let y = snapToGrid(clientY - rect.top);

        if (drawMode === "wall") {
            if (wallPoints.length > 0) {
                const snapped = snapToAngle(
                    x,
                    y,
                    wallPoints[wallPoints.length - 1],
                );
                x = snapped.x;
                y = snapped.y;
            }
            setWallPoints([...wallPoints, { x, y }]);
        } else if (placeMode) {
            placeShapeAt(x, y);
        } else {
            setSelectedShape(null);
        }
    };

    const finishWall = () => {
        if (wallPoints.length >= 2) {
            const firstPoint = wallPoints[0];
            const lastPoint = wallPoints[wallPoints.length - 1];
            const isClosed =
                firstPoint.x === lastPoint.x && firstPoint.y === lastPoint.y;

            const closedPoints = isClosed
                ? [...wallPoints]
                : [...wallPoints, firstPoint];

            setShapes([
                ...shapes,
                {
                    id: Date.now(),
                    type: "wall",
                    points: closedPoints,
                    color: "#000",
                },
            ]);
        }
        setWallPoints([]);
        setDrawMode(null);
    };

    const undoLastPoint = () => {
        if (wallPoints.length > 0) {
            setWallPoints(wallPoints.slice(0, -1));
        }
    };

    const deleteShape = () => {
        if (selectedShape) {
            setShapes(shapes.filter((s) => s.id !== selectedShape));
            setSelectedShape(null);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Escape" || e.key === "Enter") {
            if (drawMode === "wall" && wallPoints.length >= 2) {
                finishWall();
            } else {
                setWallPoints([]);
                setDrawMode(null);
                setPlaceMode(null);
            }
        } else if (e.key === "Delete" || e.key === "Backspace") {
            deleteShape();
        }
    };

    const handleMouseDown = (e, shapeId) => {
        if ((e.button !== 0 && e.type === "mousedown") || drawMode || placeMode)
            return;
        e.stopPropagation();
        setSelectedShape(shapeId);
        setIsDragging(true);
        const shape = shapes.find((s) => s.id === shapeId);
        const rect = canvasRef.current.getBoundingClientRect();
        const clientX = e.clientX;
        const clientY = e.clientY;

        if (shape.type === "wall") {
            setDragOffset({
                x: clientX - rect.left - shape.points[0].x,
                y: clientY - rect.top - shape.points[0].y,
            });
        } else {
            setDragOffset({
                x: clientX - rect.left - shape.x,
                y: clientY - rect.top - shape.y,
            });
        }
    };

    const handleTouchStart = (e, shapeId) => {
        if (drawMode || placeMode) return;
        e.preventDefault(); // Prevent synthetic mouse event
        e.stopPropagation();
        setSelectedShape(shapeId);
        setIsDragging(true);
        const shape = shapes.find((s) => s.id === shapeId);
        const rect = canvasRef.current.getBoundingClientRect();
        const clientX = e.touches && e.touches[0] && e.touches[0].clientX;
        const clientY = e.touches && e.touches[0] && e.touches[0].clientY;
        if (!clientX || !clientY) return;

        if (shape.type === "wall") {
            setDragOffset({
                x: clientX - rect.left - shape.points[0].x,
                y: clientY - rect.top - shape.points[0].y,
            });
        } else {
            setDragOffset({
                x: clientX - rect.left - shape.x,
                y: clientY - rect.top - shape.y,
            });
        }
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !selectedShape) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const clientX =
            e.clientX || (e.touches && e.touches[0] && e.touches[0].clientX);
        const clientY =
            e.clientY || (e.touches && e.touches[0] && e.touches[0].clientY);
        const x = clientX - rect.left - dragOffset.x;
        const y = clientY - rect.top - dragOffset.y;

        setShapes(
            shapes.map((s) => {
                if (s.id === selectedShape) {
                    if (s.type === "wall") {
                        const snappedX = snapToGrid(x);
                        const snappedY = snapToGrid(y);
                        const deltaX = snappedX - s.points[0].x;
                        const deltaY = snappedY - s.points[0].y;
                        return {
                            ...s,
                            points: s.points.map((p) => ({
                                x: p.x + deltaX,
                                y: p.y + deltaY,
                            })),
                        };
                    }
                    return { ...s, x, y };
                }
                return s;
            }),
        );
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const deleteAll = () => {
        setShapes([]);
        setSelectedShape(null);
        setWallPoints([]);
    };

    const rotateShape = () => {
        if (selectedShape) {
            const shape = shapes.find((s) => s.id === selectedShape);
            if (
                shape &&
                (shape.type === "door" ||
                    shape.type === "window" ||
                    shape.type === "panel" ||
                    shape.type === "magnetAccess")
            ) {
                setShapes(
                    shapes.map((s) =>
                        s.id === selectedShape
                            ? {
                                  ...s,
                                  rotation: (s.rotation || 0) + Math.PI / 2,
                              }
                            : s,
                    ),
                );
            }
        }
    };

    const flipShape = () => {
        if (selectedShape) {
            const shape = shapes.find((s) => s.id === selectedShape);
            if (shape && shape.type === "door") {
                setShapes(
                    shapes.map((s) =>
                        s.id === selectedShape
                            ? {
                                  ...s,
                                  flipped: !s.flipped,
                              }
                            : s,
                    ),
                );
            }
        }
    };

    const startDrawWall = () => {
        setDrawMode("wall");
        setWallPoints([]);
        setPlaceMode(null);
    };

    const startPlaceShape = (shape) => {
        setPlaceMode(shape);
        setDrawMode(null);
        setWallPoints([]);
    };

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawMode, wallPoints, selectedShape]);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            document.addEventListener("touchmove", handleMouseMove);
            document.addEventListener("touchend", handleMouseUp);
            return () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
                document.removeEventListener("touchmove", handleMouseMove);
                document.removeEventListener("touchend", handleMouseUp);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging, selectedShape, dragOffset]);

    return (
        <Box
            sx={{
                position: "relative",
                height: {
                    xs: "calc(100vh - 320px)",
                    sm: "calc(100vh - 260px)",
                    md: "calc(100vh - 260px)",
                },
                minHeight: 320,
                width: "100%",
                overflow: "hidden",
            }}
        >
            {/* Drawing Toolbar */}
            <Paper
                elevation={0}
                sx={{
                    position: "absolute",
                    top: { xs: 5, sm: 10 },
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 1100,
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.5, sm: 1 },
                    px: { xs: 1, sm: 2 },
                    py: { xs: 0.5, sm: 1 },
                    borderRadius: { xs: 1, sm: 2 },
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "primary.light",
                    maxWidth: "calc(100% - 16px)",
                    overflowX: "auto",
                }}
            >
                <Tooltip title="Draw Wall">
                    <IconButton
                        onClick={startDrawWall}
                        color={drawMode === "wall" ? "primary" : "default"}
                        size="small"
                        sx={{ padding: { xs: 0.5, sm: 1 } }}
                    >
                        <CreateIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    </IconButton>
                </Tooltip>
                <Tooltip
                    title={angleSnap ? "Snap to H/V (On)" : "Snap to H/V (Off)"}
                >
                    <IconButton
                        onClick={() => setAngleSnap(!angleSnap)}
                        color={angleSnap ? "primary" : "default"}
                        size="small"
                        sx={{
                            padding: { xs: 0.5, sm: 1 },
                            opacity: angleSnap ? 1 : 0.4,
                        }}
                    >
                        <StraightenIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    </IconButton>
                </Tooltip>
                <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: { xs: 0.25, sm: 0 } }}
                />
                <Tooltip title="Place Door">
                    <IconButton
                        onClick={(e) => setDoorMenuAnchor(e.currentTarget)}
                        color={
                            placeMode && placeMode.type === "door"
                                ? "primary"
                                : "default"
                        }
                        size="small"
                        sx={{
                            position: "relative",
                            padding: { xs: 0.5, sm: 1 },
                        }}
                    >
                        <MeetingRoomIcon
                            sx={{ fontSize: { xs: 20, sm: 24 } }}
                        />
                        <ArrowDropDownIcon
                            sx={{
                                position: "absolute",
                                bottom: -4,
                                right: -4,
                                fontSize: { xs: 12, sm: 14 },
                            }}
                        />
                    </IconButton>
                </Tooltip>
                <Menu
                    anchorEl={doorMenuAnchor}
                    open={Boolean(doorMenuAnchor)}
                    onClose={() => setDoorMenuAnchor(null)}
                >
                    <MenuItem
                        onClick={() => {
                            startPlaceShape({
                                type: "door",
                                width: 60,
                                height: 8,
                                color: "#FFFFFF",
                            });
                            setDoorMenuAnchor(null);
                        }}
                    >
                        Single Door
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            startPlaceShape({
                                type: "door",
                                width: 80,
                                height: 8,
                                color: "#FFFFFF",
                            });
                            setDoorMenuAnchor(null);
                        }}
                    >
                        Double Door
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            startPlaceShape({
                                type: "door",
                                width: 100,
                                height: 8,
                                color: "#FFFFFF",
                                doorType: "sliding",
                            });
                            setDoorMenuAnchor(null);
                        }}
                    >
                        Sliding Door
                    </MenuItem>
                </Menu>
                <Tooltip title="Place Window">
                    <IconButton
                        onClick={(e) => setWindowMenuAnchor(e.currentTarget)}
                        color={
                            placeMode && placeMode.type === "window"
                                ? "primary"
                                : "default"
                        }
                        size="small"
                        sx={{
                            position: "relative",
                            padding: { xs: 0.5, sm: 1 },
                        }}
                    >
                        <WindowIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        <ArrowDropDownIcon
                            sx={{
                                position: "absolute",
                                bottom: -4,
                                right: -4,
                                fontSize: { xs: 12, sm: 14 },
                            }}
                        />
                    </IconButton>
                </Tooltip>
                <Menu
                    anchorEl={windowMenuAnchor}
                    open={Boolean(windowMenuAnchor)}
                    onClose={() => setWindowMenuAnchor(null)}
                >
                    <MenuItem
                        onClick={() => {
                            startPlaceShape({
                                type: "window",
                                width: 40,
                                height: 12,
                                color: "#D3D3D3",
                            });
                            setWindowMenuAnchor(null);
                        }}
                    >
                        Small Window
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            startPlaceShape({
                                type: "window",
                                width: 80,
                                height: 12,
                                color: "#D3D3D3",
                            });
                            setWindowMenuAnchor(null);
                        }}
                    >
                        Medium Window
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            startPlaceShape({
                                type: "window",
                                width: 120,
                                height: 12,
                                color: "#D3D3D3",
                            });
                            setWindowMenuAnchor(null);
                        }}
                    >
                        Large Window
                    </MenuItem>
                </Menu>
                <Tooltip title="Pen Panel">
                    <IconButton
                        onClick={() =>
                            startPlaceShape({
                                type: "panel",
                                width: 40,
                                height: 40,
                                color: "#D3D3D3",
                            })
                        }
                        color={
                            placeMode && placeMode.type === "panel"
                                ? "primary"
                                : "default"
                        }
                        size="small"
                        sx={{
                            position: "relative",
                            padding: { xs: 0.5, sm: 1 },
                        }}
                    >
                        <SquareIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        <Typography
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                fontSize: { xs: "8px", sm: "10px" },
                                fontWeight: "bold",
                                color: "background.paper",
                                pointerEvents: "none",
                            }}
                        >
                            PP
                        </Typography>
                    </IconButton>
                </Tooltip>
                <Tooltip title="Magnet Access">
                    <IconButton
                        onClick={() =>
                            startPlaceShape({
                                type: "magnetAccess",
                                width: 80,
                                height: 12,
                                color: "#D3D3D3",
                            })
                        }
                        color={
                            placeMode && placeMode.type === "magnetAccess"
                                ? "primary"
                                : "default"
                        }
                        size="small"
                        sx={{
                            position: "relative",
                            padding: { xs: 0.5, sm: 1 },
                        }}
                    >
                        <SquareIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        <Typography
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                fontSize: { xs: "8px", sm: "10px" },
                                fontWeight: "bold",
                                color: "background.paper",
                                pointerEvents: "none",
                            }}
                        >
                            MA
                        </Typography>
                    </IconButton>
                </Tooltip>
                <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: { xs: 0.25, sm: 0 } }}
                />
                <Tooltip title="Rotate Selected">
                    <span>
                        <IconButton
                            onClick={rotateShape}
                            disabled={!selectedShape}
                            size="small"
                            sx={{ padding: { xs: 0.5, sm: 1 } }}
                        >
                            <RotateRightIcon
                                sx={{ fontSize: { xs: 20, sm: 24 } }}
                            />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Flip Swing">
                    <span>
                        <IconButton
                            onClick={flipShape}
                            disabled={!selectedShape}
                            size="small"
                            sx={{ padding: { xs: 0.5, sm: 1 } }}
                        >
                            <FlipIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Delete">
                    <span>
                        <IconButton
                            onClick={(e) =>
                                setDeleteMenuAnchor(e.currentTarget)
                            }
                            disabled={shapes.length === 0}
                            size="small"
                            color="error"
                            sx={{
                                position: "relative",
                                padding: { xs: 0.5, sm: 1 },
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            <ArrowDropDownIcon
                                sx={{
                                    position: "absolute",
                                    bottom: -4,
                                    right: -4,
                                    fontSize: { xs: 12, sm: 14 },
                                }}
                            />
                        </IconButton>
                    </span>
                </Tooltip>
                <Menu
                    anchorEl={deleteMenuAnchor}
                    open={Boolean(deleteMenuAnchor)}
                    onClose={() => setDeleteMenuAnchor(null)}
                >
                    <MenuItem
                        onClick={() => {
                            deleteShape();
                            setDeleteMenuAnchor(null);
                        }}
                        disabled={!selectedShape}
                    >
                        Delete Selected
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            setDeleteConfirmOpen(true);
                            setDeleteMenuAnchor(null);
                        }}
                    >
                        Delete All
                    </MenuItem>
                </Menu>
            </Paper>

            <Box
                ref={canvasRef}
                onClick={handleCanvasClick}
                onTouchStart={handleCanvasTouch}
                sx={{
                    width: "100%",
                    height: "100%",
                    position: "relative",
                    bgcolor: "#fff",
                    backgroundImage: `
                        repeating-linear-gradient(0deg, #e0e0e0 0px, #e0e0e0 1px, transparent 1px, transparent ${GRID_SIZE}px),
                        repeating-linear-gradient(90deg, #e0e0e0 0px, #e0e0e0 1px, transparent 1px, transparent ${GRID_SIZE}px)
                    `,
                    cursor:
                        drawMode === "wall"
                            ? "crosshair"
                            : placeMode
                            ? "copy"
                            : "default",
                    touchAction: "none",
                }}
            >
                {/* North Arrow Watermark */}
                <Box
                    sx={{
                        position: "absolute",
                        top: { xs: "50%", sm: "55%" },
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "200px", sm: "300px" },
                        height: { xs: "200px", sm: "300px" },
                        opacity: 0.08,
                        pointerEvents: "none",
                        zIndex: 0,
                        backgroundImage: `url(${northArrow})`,
                        backgroundSize: "contain",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                    }}
                />

                {(drawMode === "wall" || placeMode) && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: { xs: 45, sm: 70 },
                            left: { xs: 5, sm: 10 },
                            right: { xs: 5, sm: 10 },
                            bgcolor: "rgba(33, 150, 243, 0.95)",
                            color: "white",
                            px: { xs: 1, sm: 2 },
                            py: { xs: 0.5, sm: 1 },
                            borderRadius: 1,
                            zIndex: 100,
                            maxWidth: { xs: "calc(100% - 10px)", sm: "auto" },
                        }}
                    >
                        <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                                fontSize: { xs: "0.7rem", sm: "0.875rem" },
                                lineHeight: 1.3,
                            }}
                        >
                            {drawMode === "wall"
                                ? `Tap to add points (${wallPoints.length}) • Press Enter to finish`
                                : `Tap to place ${placeMode.type}`}
                        </Typography>
                        {drawMode === "wall" && (
                            <Typography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    mt: 0.5,
                                    fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                    opacity: 0.9,
                                }}
                            >
                                {angleSnap
                                    ? "✓ Snap to Horizontal/Vertical"
                                    : "⚠ Free angle mode"}
                            </Typography>
                        )}
                    </Box>
                )}

                {wallPoints.map((point, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: "absolute",
                            left: point.x - 4,
                            top: point.y - 4,
                            width: 8,
                            height: 8,
                            bgcolor: "#2196f3",
                            borderRadius: "50%",
                            zIndex: 50,
                        }}
                    />
                ))}

                {wallPoints.length >= 1 && (
                    <svg
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            pointerEvents: "none",
                            zIndex: 2,
                        }}
                    >
                        {wallPoints.map((point, i) => {
                            if (i === 0) return null;
                            return (
                                <line
                                    key={i}
                                    x1={wallPoints[i - 1].x}
                                    y1={wallPoints[i - 1].y}
                                    x2={point.x}
                                    y2={point.y}
                                    stroke="#2196f3"
                                    strokeWidth="4"
                                    strokeDasharray="5,5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            );
                        })}
                    </svg>
                )}

                <svg
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        pointerEvents: "none",
                        zIndex: 5,
                    }}
                >
                    {shapes.map((shape) => {
                        if (shape.type === "door") {
                            const rotation = shape.rotation || 0;
                            const doorWidth = shape.width;
                            const doorHeight = shape.height;
                            const centerX = shape.x + doorWidth / 2;
                            const centerY = shape.y + doorHeight / 2;

                            // Check if this is a sliding door
                            const isSlidingDoor = shape.doorType === "sliding";
                            // Check if this is a double door (width >= 70 and not sliding)
                            const isDoubleDoor =
                                !isSlidingDoor && doorWidth >= 70;
                            const halfWidth = doorWidth / 2;

                            // Label inside rotated group: 0° aligns with door, 180° flips it
                            const rotationDeg =
                                ((rotation * 180) / Math.PI) % 360;
                            const normalizedDeg =
                                rotationDeg < 0
                                    ? rotationDeg + 360
                                    : rotationDeg;
                            let labelRotation = 0;
                            if (normalizedDeg > 90 && normalizedDeg <= 270) {
                                labelRotation = 180;
                            }

                            return (
                                <g key={shape.id}>
                                    <g
                                        transform={`rotate(${(rotation * 180) /
                                            Math.PI} ${centerX} ${centerY})`}
                                    >
                                        {/* Door frame/wall thickness */}
                                        <rect
                                            x={shape.x}
                                            y={shape.y}
                                            width={doorWidth}
                                            height={doorHeight}
                                            fill="#FFFFFF"
                                            stroke={
                                                selectedShape === shape.id
                                                    ? "#2196f3"
                                                    : "#333"
                                            }
                                            strokeWidth={
                                                selectedShape === shape.id
                                                    ? "2"
                                                    : "1"
                                            }
                                            style={{
                                                cursor: "move",
                                                pointerEvents: "all",
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!drawMode && !placeMode) {
                                                    setSelectedShape(shape.id);
                                                }
                                            }}
                                            onMouseDown={(e) => {
                                                e.stopPropagation();
                                                handleMouseDown(e, shape.id);
                                            }}
                                            onTouchStart={(e) => {
                                                e.stopPropagation();
                                                handleTouchStart(e, shape.id);
                                            }}
                                        />
                                        {/* Door swing arc(s) and panel line(s) OR sliding door track */}
                                        {isSlidingDoor ? (
                                            <>
                                                {/* SLIDING DOOR */}
                                                <line
                                                    x1={shape.x}
                                                    y1={shape.y}
                                                    x2={shape.x + doorWidth}
                                                    y2={shape.y}
                                                    stroke={
                                                        selectedShape ===
                                                        shape.id
                                                            ? "#2196f3"
                                                            : "#666"
                                                    }
                                                    strokeWidth="2"
                                                />
                                                <rect
                                                    x={shape.x + 5}
                                                    y={shape.y}
                                                    width={halfWidth - 7}
                                                    height={doorHeight}
                                                    fill="none"
                                                    stroke={
                                                        selectedShape ===
                                                        shape.id
                                                            ? "#2196f3"
                                                            : "#666"
                                                    }
                                                    strokeWidth="2"
                                                />
                                                <rect
                                                    x={shape.x + halfWidth + 2}
                                                    y={shape.y}
                                                    width={halfWidth - 7}
                                                    height={doorHeight}
                                                    fill="none"
                                                    stroke={
                                                        selectedShape ===
                                                        shape.id
                                                            ? "#2196f3"
                                                            : "#666"
                                                    }
                                                    strokeWidth="2"
                                                />
                                                <path
                                                    d={`M ${shape.x +
                                                        halfWidth /
                                                            2} ${shape.y +
                                                        doorHeight +
                                                        10} L ${shape.x +
                                                        10} ${shape.y +
                                                        doorHeight +
                                                        10} M ${shape.x +
                                                        10} ${shape.y +
                                                        doorHeight +
                                                        10} L ${shape.x +
                                                        15} ${shape.y +
                                                        doorHeight +
                                                        5} M ${shape.x +
                                                        10} ${shape.y +
                                                        doorHeight +
                                                        10} L ${shape.x +
                                                        15} ${shape.y +
                                                        doorHeight +
                                                        15}`}
                                                    stroke="#333"
                                                    strokeWidth="2"
                                                    fill="none"
                                                />
                                                <path
                                                    d={`M ${shape.x +
                                                        halfWidth +
                                                        halfWidth /
                                                            2} ${shape.y +
                                                        doorHeight +
                                                        10} L ${shape.x +
                                                        doorWidth -
                                                        10} ${shape.y +
                                                        doorHeight +
                                                        10} M ${shape.x +
                                                        doorWidth -
                                                        10} ${shape.y +
                                                        doorHeight +
                                                        10} L ${shape.x +
                                                        doorWidth -
                                                        15} ${shape.y +
                                                        doorHeight +
                                                        5} M ${shape.x +
                                                        doorWidth -
                                                        10} ${shape.y +
                                                        doorHeight +
                                                        10} L ${shape.x +
                                                        doorWidth -
                                                        15} ${shape.y +
                                                        doorHeight +
                                                        15}`}
                                                    stroke="#333"
                                                    strokeWidth="2"
                                                    fill="none"
                                                />
                                            </>
                                        ) : isDoubleDoor ? (
                                            <>
                                                {/* LEFT DOOR - same as single but half size */}
                                                <path
                                                    d={
                                                        shape.flipped
                                                            ? `M ${shape.x +
                                                                  halfWidth} ${shape.y +
                                                                  doorHeight /
                                                                      2} A ${halfWidth} ${halfWidth} 0 0 1 ${
                                                                  shape.x
                                                              } ${shape.y +
                                                                  doorHeight /
                                                                      2 +
                                                                  halfWidth}`
                                                            : `M ${shape.x +
                                                                  halfWidth} ${shape.y +
                                                                  doorHeight /
                                                                      2} A ${halfWidth} ${halfWidth} 0 0 0 ${
                                                                  shape.x
                                                              } ${shape.y +
                                                                  doorHeight /
                                                                      2 -
                                                                  halfWidth}`
                                                    }
                                                    fill="none"
                                                    stroke={
                                                        selectedShape ===
                                                        shape.id
                                                            ? "#2196f3"
                                                            : "#666"
                                                    }
                                                    strokeWidth="1.5"
                                                    strokeDasharray="3,3"
                                                />
                                                <line
                                                    x1={shape.x}
                                                    y1={
                                                        shape.y + doorHeight / 2
                                                    }
                                                    x2={shape.x}
                                                    y2={
                                                        shape.flipped
                                                            ? shape.y +
                                                              doorHeight / 2 +
                                                              halfWidth
                                                            : shape.y +
                                                              doorHeight / 2 -
                                                              halfWidth
                                                    }
                                                    stroke={
                                                        selectedShape ===
                                                        shape.id
                                                            ? "#2196f3"
                                                            : "#333"
                                                    }
                                                    strokeWidth="2"
                                                />
                                                {/* RIGHT DOOR - mirrored */}
                                                <path
                                                    d={
                                                        shape.flipped
                                                            ? `M ${shape.x +
                                                                  halfWidth} ${shape.y +
                                                                  doorHeight /
                                                                      2} A ${halfWidth} ${halfWidth} 0 0 0 ${shape.x +
                                                                  doorWidth} ${shape.y +
                                                                  doorHeight /
                                                                      2 +
                                                                  halfWidth}`
                                                            : `M ${shape.x +
                                                                  halfWidth} ${shape.y +
                                                                  doorHeight /
                                                                      2} A ${halfWidth} ${halfWidth} 0 0 1 ${shape.x +
                                                                  doorWidth} ${shape.y +
                                                                  doorHeight /
                                                                      2 -
                                                                  halfWidth}`
                                                    }
                                                    fill="none"
                                                    stroke={
                                                        selectedShape ===
                                                        shape.id
                                                            ? "#2196f3"
                                                            : "#666"
                                                    }
                                                    strokeWidth="1.5"
                                                    strokeDasharray="3,3"
                                                />
                                                <line
                                                    x1={shape.x + doorWidth}
                                                    y1={
                                                        shape.y + doorHeight / 2
                                                    }
                                                    x2={shape.x + doorWidth}
                                                    y2={
                                                        shape.flipped
                                                            ? shape.y +
                                                              doorHeight / 2 +
                                                              halfWidth
                                                            : shape.y +
                                                              doorHeight / 2 -
                                                              halfWidth
                                                    }
                                                    stroke={
                                                        selectedShape ===
                                                        shape.id
                                                            ? "#2196f3"
                                                            : "#333"
                                                    }
                                                    strokeWidth="2"
                                                />
                                            </>
                                        ) : (
                                            <>
                                                {/* Single door swing arc */}
                                                <path
                                                    d={
                                                        shape.flipped
                                                            ? `M ${shape.x +
                                                                  doorWidth} ${shape.y +
                                                                  doorHeight /
                                                                      2}
                                                        A ${doorWidth} ${doorWidth} 0 0 1 ${
                                                                  shape.x
                                                              } ${shape.y +
                                                                  doorHeight /
                                                                      2 +
                                                                  doorWidth}`
                                                            : `M ${shape.x +
                                                                  doorWidth} ${shape.y +
                                                                  doorHeight /
                                                                      2}
                                                        A ${doorWidth} ${doorWidth} 0 0 0 ${
                                                                  shape.x
                                                              } ${shape.y +
                                                                  doorHeight /
                                                                      2 -
                                                                  doorWidth}`
                                                    }
                                                    fill="none"
                                                    stroke={
                                                        selectedShape ===
                                                        shape.id
                                                            ? "#2196f3"
                                                            : "#666"
                                                    }
                                                    strokeWidth="1.5"
                                                    strokeDasharray="3,3"
                                                />
                                                {/* Single door panel line */}
                                                <line
                                                    x1={shape.x}
                                                    y1={
                                                        shape.y + doorHeight / 2
                                                    }
                                                    x2={shape.x}
                                                    y2={
                                                        shape.flipped
                                                            ? shape.y +
                                                              doorHeight / 2 +
                                                              doorWidth
                                                            : shape.y +
                                                              doorHeight / 2 -
                                                              doorWidth
                                                    }
                                                    stroke={
                                                        selectedShape ===
                                                        shape.id
                                                            ? "#2196f3"
                                                            : "#333"
                                                    }
                                                    strokeWidth="2"
                                                />
                                            </>
                                        )}
                                        {/* Door label - inside rotated group */}
                                        <text
                                            x={shape.x + doorWidth / 2}
                                            y={
                                                isSlidingDoor
                                                    ? shape.y - 10
                                                    : isDoubleDoor &&
                                                      !shape.flipped
                                                    ? shape.y + doorHeight + 15
                                                    : shape.y - 10
                                            }
                                            fontSize="11"
                                            fontWeight="bold"
                                            fill="#333"
                                            textAnchor="middle"
                                            transform={`rotate(${labelRotation} ${shape.x +
                                                doorWidth / 2} ${
                                                isSlidingDoor
                                                    ? shape.y - 10
                                                    : isDoubleDoor &&
                                                      !shape.flipped
                                                    ? shape.y + doorHeight + 15
                                                    : shape.y - 10
                                            })`}
                                            style={{ pointerEvents: "none" }}
                                        >
                                            {isSlidingDoor
                                                ? "SLIDING DOOR"
                                                : "DOOR"}
                                        </text>
                                    </g>
                                </g>
                            );
                        }
                        return null;
                    })}
                </svg>

                {shapes.map((shape) => {
                    if (shape.type === "wall") {
                        const points = shape.points
                            .map((p) => `${p.x},${p.y}`)
                            .join(" ");
                        return (
                            <svg
                                key={shape.id}
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    pointerEvents: "none",
                                    zIndex: 1,
                                }}
                            >
                                {/* Dark border line (thicker) */}
                                <polyline
                                    points={points}
                                    fill="none"
                                    stroke={
                                        selectedShape === shape.id
                                            ? "#2196f3"
                                            : "#333"
                                    }
                                    strokeWidth={
                                        selectedShape === shape.id ? "10" : "8"
                                    }
                                    strokeLinecap="square"
                                    strokeLinejoin="miter"
                                />
                                {/* White center line (thinner) */}
                                <polyline
                                    points={points}
                                    fill="none"
                                    stroke="#FFFFFF"
                                    strokeWidth={
                                        selectedShape === shape.id ? "6" : "4"
                                    }
                                    strokeLinecap="square"
                                    strokeLinejoin="miter"
                                    style={{
                                        cursor: "pointer",
                                        pointerEvents: "stroke",
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!drawMode && !placeMode)
                                            setSelectedShape(shape.id);
                                    }}
                                    onMouseDown={(e) => {
                                        e.stopPropagation();
                                        if (!drawMode && !placeMode)
                                            handleMouseDown(e, shape.id);
                                    }}
                                    onTouchStart={(e) => {
                                        e.stopPropagation();
                                        if (!drawMode && !placeMode)
                                            handleTouchStart(e, shape.id);
                                    }}
                                />
                            </svg>
                        );
                    }

                    // Skip doors as they're rendered in the SVG above
                    if (shape.type === "door") {
                        return null;
                    }

                    const rotation = shape.rotation || 0;

                    // Label rotates with shape but flips if upside-down
                    const rotationDeg = ((rotation * 180) / Math.PI) % 360;
                    const normalizedDeg =
                        rotationDeg < 0 ? rotationDeg + 360 : rotationDeg;
                    let labelRotation = 0;
                    if (normalizedDeg > 90 && normalizedDeg <= 270) {
                        labelRotation = Math.PI;
                    }

                    return (
                        <Box
                            key={shape.id}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!drawMode && !placeMode) {
                                    setSelectedShape(shape.id);
                                }
                            }}
                            onMouseDown={(e) => handleMouseDown(e, shape.id)}
                            onTouchStart={(e) => handleTouchStart(e, shape.id)}
                            sx={{
                                position: "absolute",
                                left: shape.x,
                                top: shape.y,
                                width: shape.width,
                                height: shape.height,
                                bgcolor: shape.color,
                                border:
                                    selectedShape === shape.id
                                        ? "2px solid #2196f3"
                                        : "1px solid #333",
                                cursor: "move",
                                boxShadow:
                                    selectedShape === shape.id
                                        ? "0 0 10px rgba(33,150,243,0.5)"
                                        : "none",
                                transform: `rotate(${rotation}rad)`,
                                transformOrigin: "center",
                                "&:hover": { opacity: 0.9 },
                                zIndex:
                                    shape.type === "door" ||
                                    shape.type === "window"
                                        ? 10
                                        : 5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold",
                                fontSize: "18px",
                                color: "#fff",
                                userSelect: "none",
                            }}
                        >
                            {shape.type === "panel" && (
                                <Box
                                    sx={{
                                        transform: `rotate(${labelRotation}rad)`,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        lineHeight: 1,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "11px",
                                            fontWeight: "bold",
                                            color: "#333",
                                        }}
                                    >
                                        PEN
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "11px",
                                            fontWeight: "bold",
                                            color: "#333",
                                        }}
                                    >
                                        PANEL
                                    </Typography>
                                </Box>
                            )}
                            {shape.type === "magnetAccess" && (
                                <Typography
                                    sx={{
                                        position: "absolute",
                                        top: -20,
                                        left: "50%",
                                        transform: `translateX(-50%) rotate(${labelRotation}rad)`,
                                        fontSize: "11px",
                                        fontWeight: "bold",
                                        color: "#333",
                                        pointerEvents: "none",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    MAGNET ACCESS
                                </Typography>
                            )}
                            {/* Label for windows */}
                            {shape.type === "window" && (
                                <Typography
                                    sx={{
                                        position: "absolute",
                                        top: -20,
                                        left: "50%",
                                        transform: `translateX(-50%) rotate(${labelRotation}rad)`,
                                        fontSize: "11px",
                                        fontWeight: "bold",
                                        color: "#333",
                                        pointerEvents: "none",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    WINDOW
                                </Typography>
                            )}
                        </Box>
                    );
                })}

                {wallPoints.length >= 1 && (
                    <Box
                        sx={{
                            position: "fixed",
                            bottom: { xs: 80, sm: 90 },
                            left: "50%",
                            transform: "translateX(-50%)",
                            display: "flex",
                            gap: 2,
                            zIndex: 1200,
                        }}
                    >
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                const now = Date.now();
                                if (now - lastInteractionTime.current < 300)
                                    return;
                                lastInteractionTime.current = now;
                                undoLastPoint();
                            }}
                            onTouchStart={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const now = Date.now();
                                if (now - lastInteractionTime.current < 300)
                                    return;
                                lastInteractionTime.current = now;
                                undoLastPoint();
                            }}
                            sx={{
                                bgcolor: "#ff9800",
                                color: "white",
                                px: { xs: 2, sm: 3 },
                                py: { xs: 1, sm: 1.5 },
                                borderRadius: { xs: 1, sm: 2 },
                                cursor: "pointer",
                                fontWeight: "bold",
                                fontSize: { xs: "0.8rem", sm: "1rem" },
                                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                                "&:hover": { bgcolor: "#f57c00" },
                            }}
                        >
                            Undo
                        </Box>
                        {wallPoints.length >= 2 && (
                            <Box
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const now = Date.now();
                                    if (now - lastInteractionTime.current < 300)
                                        return;
                                    lastInteractionTime.current = now;
                                    finishWall();
                                }}
                                onTouchStart={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const now = Date.now();
                                    if (now - lastInteractionTime.current < 300)
                                        return;
                                    lastInteractionTime.current = now;
                                    finishWall();
                                }}
                                sx={{
                                    bgcolor: "#4caf50",
                                    color: "white",
                                    px: { xs: 2, sm: 3 },
                                    py: { xs: 1, sm: 1.5 },
                                    borderRadius: { xs: 1, sm: 2 },
                                    cursor: "pointer",
                                    fontWeight: "bold",
                                    fontSize: { xs: "0.8rem", sm: "1rem" },
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                                    "&:hover": { bgcolor: "#45a049" },
                                }}
                            >
                                Finish Wall
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
            <DeleteConfirmDialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={() => {
                    deleteAll();
                    setDeleteConfirmOpen(false);
                }}
            />
        </Box>
    );
};

export default SimpleFloorPlan;
